import { useEffect } from 'react'

const CRISP_ID = 'f02b4819-fc6c-445d-a328-3898a4f73b2e'
const LS_KEY = 'ambunet_support_chat_enabled'
const LS_DISMISSED_KEY = 'ambunet_support_chat_dismissed'
const LS_FORCED_OPEN_KEY = 'ambunet_support_chat_forced_open'
const SCRIPT_SRC = 'https://client.crisp.chat/l.js'

const safeStr = (v, max = 80) => {
  if (v === null || v === undefined) return ''
  const s = String(v).trim()
  return s.length > max ? s.slice(0, max - 1) + '…' : s
}

const isValidEmail = (email) =>
  typeof email === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

// Fired by openCrispChat so a mounted useCrisp wires up the chat it just
// loaded (identity, events).
const CHAT_REQUESTED_EVENT = 'ambunet:support-chat-requested'

// Crisp is loaded only once someone has asked for support chat (the Support
// page button, "Contact AmbuNet", etc. — openCrispChat), so no chat script
// runs and no chat cookie is set for anyone who hasn't. While a conversation
// is recent (LS_KEY / LS_FORCED_OPEN_KEY, set by openCrispChat and by a
// reply arriving) it loads on every page so replies still reach them.
const chatWanted = () => {
  try {
    return (
      localStorage.getItem(LS_KEY) === '1' ||
      localStorage.getItem(LS_FORCED_OPEN_KEY) === '1'
    )
  } catch {
    return false
  }
}

export default function useCrisp(user, tenantInfo, isLoggedIn) {
  useEffect(() => {
    if (!isLoggedIn || !user || !tenantInfo) return

    let interval = null
    let started = false

    const identify = () => {
      if (!window.$crisp) return

      // Demo tenants: the "logged-in user" is a shared spoof persona (Sarah
      // Ellison et al) — never present it to support as the visitor's
      // identity. Leave the visitor anonymous so Crisp asks who they are
      // (see the chat:opened prompt below), and tag the session as demo so
      // support has the context.
      if (tenantInfo?.isDemo) {
        try {
          const storedEmail = window.$crisp.get?.('user:email')
          if (
            typeof storedEmail === 'string' &&
            storedEmail.endsWith('@demo.invalid')
          ) {
            // This browser was identified as a persona before this fix —
            // wipe the polluted Crisp session (history there is persona-
            // labelled anyway)
            window.$crisp.push(['do', 'session:reset'])
          }
        } catch {}

        const personaName = safeStr(
          `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim(),
          60,
        )
        window.$crisp.push([
          'set',
          'session:data',
          [
            [
              ['demo_visitor', 'true'],
              ['demo_persona', personaName || 'unknown'],
              ['tenant_subdomain', safeStr(window.location.hostname, 120)],
            ],
          ],
        ])
        return
      }

      const email = safeStr(user?.email, 120)
      if (isValidEmail(email)) {
        window.$crisp.push(['set', 'user:email', [email]])
      }

      const name = safeStr(
        `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim(),
        60,
      )
      if (name) {
        window.$crisp.push(['set', 'user:nickname', [name]])
      }

      // Use "safe" keys (no spaces) + safe strings
      const pairs = []
      if (tenantInfo?.subdomain)
        pairs.push(['tenant_subdomain', safeStr(tenantInfo.subdomain, 120)])
      if (user?._id) pairs.push(['user_id', safeStr(user._id, 80)])

      // Only push if we actually have data
      if (pairs.length) {
        // ✅ Most compatible shape: [ [key, value], [key2, value2] ]
        window.$crisp.push(['set', 'session:data', [pairs]])
      }
    }

    const start = () => {
    if (started) return
    started = true

    // Ensure crisp globals exist (do NOT overwrite if already set)
    window.$crisp = window.$crisp || []
    window.CRISP_WEBSITE_ID = CRISP_ID

    // Inject script once
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`)
    if (!existing) {
      const script = document.createElement('script')
      script.src = SCRIPT_SRC
      script.async = true
      document.head.appendChild(script)
    }

    let tries = 0
    const maxTries = 250 // ~25s

    interval = setInterval(() => {
      tries++

      // Crisp is ready when `$crisp.is` exists
      if (window.$crisp?.is) {
        clearInterval(interval)

        // Identify immediately now that it is ready
        identify()

        // Wire events only once across SPA navigation
        if (!window.__ambunetCrispWired) {
          window.__ambunetCrispWired = true

          window.$crisp.push(['on', 'chat:loaded', identify])
          window.$crisp.push([
            'on',
            'chat:opened',
            () => {
              try {
                localStorage.removeItem(LS_DISMISSED_KEY)
              } catch {}
              identify()

              // Demo visitors are anonymous by design — ask who they are the
              // first time they open the chat (once per page load; Crisp's
              // own email prompt may also appear per dashboard settings)
              if (tenantInfo?.isDemo && !window.__ambunetCrispDemoAsked) {
                try {
                  const knownEmail = window.$crisp.get?.('user:email')
                  if (!knownEmail) {
                    window.__ambunetCrispDemoAsked = true
                    window.$crisp.push([
                      'do',
                      'message:show',
                      [
                        'text',
                        "Hi! 👋 You're chatting from the AmbuNet demo, so we don't know who you are — could you share your name and email? Then ask us anything.",
                      ],
                    ])
                  }
                } catch {}
              }
            },
          ])

          // Hide again when chat closes + clear your "enabled" flag
          window.$crisp.push([
            'on',
            'chat:closed',
            () => {
              try {
                const hideAt = Date.now() + 20 * 60 * 1000
                localStorage.setItem(LS_DISMISSED_KEY, String(hideAt))
                localStorage.removeItem(LS_FORCED_OPEN_KEY)
              } catch {}

              // Keep the Crisp launcher visible after closing the panel
              window.$crisp?.push(['do', 'chat:show'])

              // Hide it after 20 minutes if the page is still open
              window.setTimeout(
                () => {
                  try {
                    const dismissedUntil = Number(
                      localStorage.getItem(LS_DISMISSED_KEY),
                    )
                    if (dismissedUntil && Date.now() >= dismissedUntil) {
                      window.$crisp?.push(['do', 'chat:hide'])
                      localStorage.removeItem(LS_KEY)
                    }
                  } catch {
                    window.$crisp?.push(['do', 'chat:hide'])
                  }
                },
                20 * 60 * 1000,
              )
            },
          ])

          window.$crisp.push([
            'on',
            'message:received',
            () => {
              try {
                localStorage.setItem(LS_FORCED_OPEN_KEY, '1')
                localStorage.removeItem(LS_DISMISSED_KEY)
              } catch {}

              window.$crisp.push(['do', 'chat:show'])
              window.$crisp.push(['do', 'chat:open'])
            },
          ])
        }

        const enabled = localStorage.getItem(LS_KEY) === '1'
        const forcedOpen = localStorage.getItem(LS_FORCED_OPEN_KEY) === '1'
        const dismissed = localStorage.getItem(LS_DISMISSED_KEY) === '1'

        window.$crisp.push([
          'do',
          (enabled || forcedOpen) && !dismissed ? 'chat:show' : 'chat:hide',
        ])
      }

      if (tries >= maxTries) clearInterval(interval)
    }, 100)
    }

    const alreadyLoaded = !!document.querySelector(`script[src="${SCRIPT_SRC}"]`)
    if (alreadyLoaded || chatWanted()) start()
    window.addEventListener(CHAT_REQUESTED_EVENT, start)

    return () => {
      window.removeEventListener(CHAT_REQUESTED_EVENT, start)
      if (interval) clearInterval(interval)
      // Don’t reset session on route change; just hide the widget.
      window.$crisp?.push(['do', 'chat:hide'])
    }
  }, [
    isLoggedIn,
    user?._id,
    user?.email,
    user?.firstName,
    user?.lastName,
    tenantInfo?.subdomain,
    tenantInfo?.isDemo,
  ])
}

/**
 * Helper you can call from your Support page button.
 * It waits until Crisp is ready, then shows + opens the chat.
 */
export function openCrispChat() {
  try {
    localStorage.setItem(LS_KEY, '1')
    localStorage.removeItem(LS_DISMISSED_KEY)
    localStorage.setItem(LS_FORCED_OPEN_KEY, '1')
  } catch {}

  window.$crisp = window.$crisp || []
  window.CRISP_WEBSITE_ID = window.CRISP_WEBSITE_ID || CRISP_ID

  // Let a mounted useCrisp identify the user and wire its events.
  try {
    window.dispatchEvent(new Event(CHAT_REQUESTED_EVENT))
  } catch {}

  const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`)

  if (!existing) {
    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.async = true
    script.id = 'crisp-chat-script'
    document.head.appendChild(script)
  }

  const open = () => {
    window.$crisp.push(['do', 'chat:show'])
    window.$crisp.push(['do', 'chat:open'])
  }

  if (window.$crisp?.is) {
    open()
    return
  }

  let tries = 0
  const maxTries = 200

  const interval = setInterval(() => {
    tries++

    if (window.$crisp?.is) {
      clearInterval(interval)
      open()
    }

    if (tries >= maxTries) {
      clearInterval(interval)
      window.open('mailto:support@ambunet.co.uk', '_blank')
    }
  }, 100)
}
